import { createFileRoute, Link } from "@tanstack/react-router";
import { useTenant } from "../lib/tenant-context";

export const Route = createFileRoute("/settings")({
  component: Settings,
});

function Settings() {
  const { currentTenant } = useTenant();

  // Get tenant-specific settings
  const tenantSettings = getTenantSettings(currentTenant);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-h3 text-penta-black">Settings</h1>
        <p className="text-body text-penta-grey-medium mt-1">
          Manage your organisation and preferences
        </p>
      </div>

      {/* Settings Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar */}
        <div className="space-y-1">
          <SettingsNavItem label="Organisation" active />
          <Link to="/access-requests" className="block">
            <SettingsNavItem label="Team Members" />
          </Link>
          <SettingsNavItem label="Billing" />
          <SettingsNavItem label="Webhooks" />
          <div className="pt-4 mt-4 border-t border-slate-200">
            <p className="px-4 text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">
              Integrations
            </p>
            <Link to="/settings/shopify" className="block">
              <SettingsNavItem label="Shopify" />
            </Link>
          </div>
          <div className="pt-4 mt-4 border-t border-slate-200">
            <p className="px-4 text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">
              Programs
            </p>
            <Link to="/programs" className="block">
              <SettingsNavItem label="Trade-In & Policies" />
            </Link>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Organisation Settings */}
          <div className="card">
            <h2 className="text-body text-penta-black font-medium mb-4">
              Organisation
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-penta-grey-medium mb-1">
                  Organisation Name
                </label>
                <input
                  type="text"
                  defaultValue={tenantSettings.name || currentTenant.name || ""}
                  className="w-full px-4 py-2 border border-penta-grey-medium/20"
                />
              </div>

              <div>
                <label className="block text-sm text-penta-grey-medium mb-1">
                  Slug
                </label>
                <input
                  type="text"
                  defaultValue={tenantSettings.slug || currentTenant.slug || ""}
                  className="w-full px-4 py-2 border border-penta-grey-medium/20 bg-penta-grey-light"
                  disabled
                />
                <p className="text-xs text-penta-grey-medium mt-1">
                  Used in API URLs. Contact support to change.
                </p>
              </div>

              <div>
                <label className="block text-sm text-penta-grey-medium mb-1">
                  Webhook URL
                </label>
                <input
                  type="text"
                  defaultValue={tenantSettings.webhookUrl}
                  className="w-full px-4 py-2 border border-penta-grey-medium/20 font-mono text-sm"
                  disabled
                />
                <p className="text-xs text-penta-grey-medium mt-1">
                  Endpoint for receiving trade-in events
                </p>
              </div>

              <div>
                <label className="block text-sm text-penta-grey-medium mb-1">
                  Primary Color
                </label>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 border"
                    style={{
                      backgroundColor: tenantSettings.primaryColor,
                      borderColor: "rgba(169,169,169,0.2)",
                    }}
                  />
                  <input
                    type="text"
                    defaultValue={tenantSettings.primaryColor}
                    className="flex-1 px-4 py-2 border border-penta-grey-medium/20 font-mono text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button className="btn-primary text-sm py-2">Save Changes</button>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="card border border-penta-coral/30">
            <h2 className="text-body text-penta-coral font-medium mb-4">
              Danger Zone
            </h2>
            <p className="text-sm text-penta-grey-medium mb-4">
              Permanently delete your organisation and all associated data.
            </p>
            <button className="px-4 py-2 border border-penta-coral text-penta-coral rounded-full text-sm hover:bg-penta-coral/10 transition-colors">
              Delete Organisation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsNavItem({
  label,
  active = false,
}: {
  label: string;
  active?: boolean;
}) {
  return (
    <button
      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
        active
          ? "bg-penta-black text-penta-white"
          : "text-penta-grey-medium hover:text-penta-black hover:bg-penta-grey-light"
      }`}
    >
      {label}
    </button>
  );
}

// Tenant-specific settings
function getTenantSettings(tenant: {
  id: string;
  name: string;
  slug: string;
  color?: string;
}): {
  name: string;
  slug: string;
  webhookUrl: string;
  primaryColor: string;
} {
  // Known tenant overrides (display name differs from slug)
  const overrides: Record<string, { name?: string; primaryColor?: string }> = {
    arcteryx: { name: "Arc'teryx", primaryColor: "#1a1a1a" },
    nespresso: { name: "Nespresso", primaryColor: "#6B4423" },
    lululemon: { name: "lululemon", primaryColor: "#C41230" },
  };

  const override = overrides[tenant.id] || {};

  return {
    name: override.name || tenant.name,
    slug: tenant.slug || tenant.id,
    webhookUrl: `https://api.pentatonic.com/v1/webhooks/${tenant.slug || tenant.id}`,
    primaryColor: override.primaryColor || tenant.color || "#2D2D2D",
  };
}
